const STORAGE_KEY = 'mezaldy:wishlist';
const CHANGE_EVENT = 'wishlist:change';
const ERROR_EVENT = 'wishlist:error';
const DRAWER_BATCH_SIZE = 8;
const REQUEST_CONCURRENCY = 4;
const PRODUCT_ROW_SECTION_ID = 'wishlist-product-row';

function normalizeItems(value) {
  if (!Array.isArray(value)) return [];

  const items = [];
  const indexesById = new Map();

  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;

    const id = String(entry.id ?? '').trim();
    const handle = String(entry.handle ?? '').trim();
    if (!id || !handle) continue;

    const item = { id, handle };
    const existingIndex = indexesById.get(id);

    if (existingIndex === undefined) {
      indexesById.set(id, items.length);
      items.push(item);
    } else {
      items[existingIndex] = item;
    }
  }

  return items;
}

function readItems() {
  try {
    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    if (!storedValue) return [];
    return normalizeItems(JSON.parse(storedValue));
  } catch {
    return [];
  }
}

function cloneItems(items) {
  return items.map(({ id, handle }) => ({ id, handle }));
}

function dispatchChange(items) {
  const snapshot = cloneItems(items);
  syncWishlistUI(snapshot);
  document.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, {
      detail: {
        items: snapshot,
        count: snapshot.length,
      },
    })
  );
}

function commitItems(items) {
  const normalizedItems = normalizeItems(items);

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedItems));
  } catch {
    dispatchError('');
    return false;
  }

  dispatchChange(normalizedItems);
  return true;
}

function addItem(item) {
  const [normalizedItem] = normalizeItems([item]);
  if (!normalizedItem) return false;

  const items = readItems();
  const existingIndex = items.findIndex(({ id }) => id === normalizedItem.id);

  if (existingIndex >= 0) {
    if (items[existingIndex].handle === normalizedItem.handle) {
      syncWishlistUI(items);
      return true;
    }

    items[existingIndex] = normalizedItem;
    return commitItems(items);
  }

  items.push(normalizedItem);
  return commitItems(items);
}

function removeItem(id) {
  const normalizedId = String(id ?? '').trim();
  if (!normalizedId) return false;

  const items = readItems();
  const updatedItems = items.filter((item) => item.id !== normalizedId);
  if (updatedItems.length === items.length) {
    syncWishlistUI(items);
    return true;
  }

  return commitItems(updatedItems);
}

function toggleItem(item) {
  const [normalizedItem] = normalizeItems([item]);
  if (!normalizedItem) return false;

  const items = readItems();
  return items.some(({ id }) => id === normalizedItem.id)
    ? removeItem(normalizedItem.id)
    : addItem(normalizedItem);
}

function replaceCountToken(value, count) {
  return String(value ?? '').replaceAll('__count__', String(count));
}

function syncWishlistButton(button, selectedIds) {
  const id = String(button.dataset.wishlistId ?? '');
  const isSelected = selectedIds.has(id);
  const label = isSelected ? button.dataset.wishlistRemoveLabel : button.dataset.wishlistAddLabel;

  button.setAttribute('aria-pressed', String(isSelected));
  button.classList.toggle('is-active', isSelected);

  if (label) {
    button.setAttribute('aria-label', label);
    button.title = label;
    const visibleLabel = button.querySelector('[data-wishlist-button-label]');
    if (visibleLabel) visibleLabel.textContent = label;
  }
}

function syncWishlistUI(items = readItems()) {
  const selectedIds = new Set(items.map(({ id }) => id));

  for (const button of document.querySelectorAll('[data-wishlist-id][data-wishlist-handle]')) {
    if (button instanceof HTMLButtonElement) syncWishlistButton(button, selectedIds);
  }

  for (const count of document.querySelectorAll('[data-wishlist-count]')) {
    count.textContent = String(items.length);
    count.toggleAttribute('hidden', items.length === 0);
  }
}

function dispatchError(message) {
  document.dispatchEvent(
    new CustomEvent(ERROR_EVENT, {
      detail: { message },
    })
  );
}

function handleWishlistClick(event) {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest('[data-wishlist-id][data-wishlist-handle]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();
  event.stopPropagation();

  toggleItem({
    id: button.dataset.wishlistId,
    handle: button.dataset.wishlistHandle,
  });

}

function observeWishlistButtons() {
  const observer = new MutationObserver((records) => {
    let shouldSync = false;

    for (const record of records) {
      if (record.type === 'attributes') {
        if (record.target instanceof Element && record.target.matches('[data-wishlist-id][data-wishlist-handle]')) {
          shouldSync = true;
          break;
        }
        continue;
      }

      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (
          node.matches('[data-wishlist-id][data-wishlist-handle]') ||
          node.querySelector('[data-wishlist-id][data-wishlist-handle]')
        ) {
          shouldSync = true;
          break;
        }
      }

      if (shouldSync) break;
    }

    if (shouldSync) syncWishlistUI();
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-wishlist-id', 'data-wishlist-handle'],
    childList: true,
    subtree: true,
  });
}

async function mapWithConcurrency(items, worker) {
  const results = new Array(items.length);
  let nextIndex = 0;

  async function runWorker() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await worker(items[index]);
    }
  }

  const workerCount = Math.min(REQUEST_CONCURRENCY, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
  return results;
}

function getProductSectionURL(handle) {
  const shopRoot = window.Shopify?.routes?.root || '/';
  const normalizedRoot = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;
  const url = new URL(`${normalizedRoot}products/${encodeURIComponent(handle)}`, window.location.origin);
  url.searchParams.set('section_id', PRODUCT_ROW_SECTION_ID);
  return url;
}

async function fetchProductRow(item, signal) {
  try {
    const response = await fetch(getProductSectionURL(item.handle), {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      signal,
    });

    if (response.status === 404) return { status: 'missing', item };
    if (!response.ok) return { status: 'error', item };

    const template = document.createElement('template');
    template.innerHTML = await response.text();

    if (template.content.querySelector('[data-wishlist-product-missing]')) {
      return { status: 'missing', item };
    }

    const row = template.content.querySelector('[data-wishlist-product-row][data-product-id]');
    if (!(row instanceof HTMLElement)) return { status: 'error', item };
    if (String(row.dataset.productId) !== item.id) return { status: 'missing', item };

    return { status: 'ready', item, row };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return { status: 'aborted', item };
    return { status: 'error', item };
  }
}

function itemKey(item) {
  return `${item.id}\u0000${item.handle}`;
}

class WishlistDrawerRenderer {
  constructor(root) {
    this.root = root;
    this.dialog = root.querySelector('dialog');
    this.list = root.querySelector('[data-wishlist-list]');
    this.emptyState = root.querySelector('[data-wishlist-empty]');
    this.loadingState = root.querySelector('[data-wishlist-loading]');
    this.status = root.querySelector('[data-wishlist-status]');
    this.loadMoreButton = root.querySelector('[data-wishlist-load-more]');
    this.visibleCount = DRAWER_BATCH_SIZE;
    this.renderVersion = 0;
    this.removedUnavailableCount = 0;
    this.storageError = '';
    this.abortController = null;
    this.rowCache = new Map();

    if (
      !(this.dialog instanceof HTMLDialogElement) ||
      !(this.list instanceof HTMLElement) ||
      !(this.emptyState instanceof HTMLElement) ||
      !(this.loadingState instanceof HTMLElement) ||
      !(this.status instanceof HTMLElement) ||
      !(this.loadMoreButton instanceof HTMLButtonElement)
    ) {
      return;
    }

    this.handleOpen = () => {
      this.visibleCount = DRAWER_BATCH_SIZE;
      this.removedUnavailableCount = 0;
      this.storageError = '';
      this.rowCache.clear();
      this.render();
    };
    this.handleClose = () => this.abortController?.abort();
    this.handleWishlistChange = () => {
      if (this.dialog.open) this.render();
    };
    this.handleWishlistError = (event) => {
      this.storageError = event.detail?.message || this.root.dataset.storageError || '';
      this.updateStatus(0);
    };
    this.handleLoadMore = () => {
      this.visibleCount += DRAWER_BATCH_SIZE;
      this.render();
    };

    this.root.addEventListener('dialog:open', this.handleOpen);
    this.root.addEventListener('dialog:close', this.handleClose);
    document.addEventListener(CHANGE_EVENT, this.handleWishlistChange);
    document.addEventListener(ERROR_EVENT, this.handleWishlistError);
    this.loadMoreButton.addEventListener('click', this.handleLoadMore);
  }

  updateStatus(loadErrorCount) {
    const messages = [];

    if (this.removedUnavailableCount > 0) {
      const translation =
        this.removedUnavailableCount === 1
          ? this.root.dataset.removedUnavailableOne
          : this.root.dataset.removedUnavailableOther;
      messages.push(replaceCountToken(translation, this.removedUnavailableCount));
    }

    if (loadErrorCount > 0) messages.push(this.root.dataset.loadError || '');
    if (this.storageError) messages.push(this.storageError);

    this.status.textContent = messages.filter(Boolean).join(' ');
    this.status.hidden = this.status.textContent === '';
  }

  pruneCache(items) {
    const currentKeys = new Set(items.map(itemKey));
    for (const key of this.rowCache.keys()) {
      if (!currentKeys.has(key)) this.rowCache.delete(key);
    }
  }

  async render() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const version = this.renderVersion + 1;
    this.renderVersion = version;

    const items = readItems();
    this.pruneCache(items);
    const visibleItems = items.slice(0, this.visibleCount);

    this.root.setAttribute('aria-busy', String(items.length > 0));
    this.loadMoreButton.hidden = true;

    if (items.length === 0) {
      this.list.replaceChildren();
      this.list.hidden = true;
      this.emptyState.hidden = false;
      this.loadingState.hidden = true;
      this.root.setAttribute('aria-busy', 'false');
      this.updateStatus(0);
      return;
    }

    this.emptyState.hidden = true;
    const itemsToFetch = visibleItems.filter((item) => !this.rowCache.has(itemKey(item)));
    this.loadingState.hidden = itemsToFetch.length === 0;

    const results = await mapWithConcurrency(itemsToFetch, (item) => fetchProductRow(item, signal));
    if (signal.aborted || version !== this.renderVersion) return;

    const missingIds = new Set(
      results.filter(({ status }) => status === 'missing').map(({ item }) => item.id)
    );

    if (missingIds.size > 0) {
      const currentItems = readItems();
      const updatedItems = currentItems.filter(({ id }) => !missingIds.has(id));
      const removedCount = currentItems.length - updatedItems.length;

      if (removedCount > 0) {
        this.removedUnavailableCount += removedCount;
        if (commitItems(updatedItems)) return;
        this.removedUnavailableCount -= removedCount;
        this.storageError = this.root.dataset.storageError || '';
      }
    }

    for (const result of results) {
      if (result.status === 'ready') this.rowCache.set(itemKey(result.item), result.row);
    }

    const rows = visibleItems.map((item) => this.rowCache.get(itemKey(item))).filter((row) => row instanceof HTMLElement);
    const loadErrorCount = results.filter(({ status }) => status === 'error').length;

    this.list.replaceChildren(...rows);
    this.list.hidden = rows.length === 0;
    this.loadingState.hidden = true;
    this.loadMoreButton.hidden = this.visibleCount >= items.length;
    this.root.setAttribute('aria-busy', 'false');
    this.updateStatus(loadErrorCount);
    syncWishlistUI();
  }
}

function initializeWishlist() {
  document.addEventListener('click', handleWishlistClick, true);
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    dispatchChange(readItems());
  });

  observeWishlistButtons();
  syncWishlistUI();

  for (const root of document.querySelectorAll('[data-wishlist-drawer]')) {
    if (root instanceof HTMLElement) new WishlistDrawerRenderer(root);
  }

  window.MezaldyWishlist = Object.freeze({
    eventName: CHANGE_EVENT,
    getItems: () => cloneItems(readItems()),
    add: (item) => addItem(item),
    remove: (id) => removeItem(id),
    toggle: (item) => toggleItem(item),
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeWishlist, { once: true });
} else {
  initializeWishlist();
}
